import React, { useState } from 'react';
import { Table, Card, Button, Space, message, DatePicker, Row, Col, Statistic, Typography, Select, Tag } from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Title } from '../../components/design-system/typography';
import { StatusPill, getStatusVariant } from '../../components/design-system/StatusPill';
import { colors, spacing } from '../../components/design-system/tokens';
import { reportsApi, companiesApi, ShipmentsByStatusResponse } from '../../services/api';
import dayjs, { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;
const { Text } = Typography;

type ShipmentItem = ShipmentsByStatusResponse['shipments'][0];

const STATUS_ORDER = ['PLANNED', 'BOOKED', 'LOADING', 'IN_TRANSIT', 'ARRIVED', 'DELIVERED', 'CANCELLED'];

const STATUS_COLORS: Record<string, string> = {
  PLANNED: 'default',
  BOOKED: 'processing',
  LOADING: 'processing',
  IN_TRANSIT: 'warning',
  ARRIVED: 'success',
  DELIVERED: 'success',
  CANCELLED: 'error',
};

interface Company {
  id: string;
  name: string;
  shortName?: string;
  shortCode?: string;
}

const formatShipmentDate = (start: string | null, end: string | null): string => {
  if (!start && !end) return '-';
  if (!end) return start || '-';
  if (!start) return end;
  const isShortFormat = ['Beg', 'Mid', 'By', 'End', 'Prompt'].includes(start);
  return isShortFormat ? `${start} / ${end}` : `${start} - ${end}`;
};

const ShipmentsByStatus: React.FC = () => {
  const navigate = useNavigate();
  const [etaRange, setEtaRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [shipmentRange, setShipmentRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<string | undefined>(undefined);
  const [selectedBuyer, setSelectedBuyer] = useState<string | undefined>(undefined);

  const { data: suppliersData } = useQuery({
    queryKey: ['companies', 'supplier'],
    queryFn: () => companiesApi.list({ type: 'supplier', active: true, limit: 100 }),
  });
  const suppliers: Company[] = (suppliersData as { companies?: Company[] })?.companies || [];

  const { data: buyersData } = useQuery({
    queryKey: ['companies', 'buyer'],
    queryFn: () => companiesApi.list({ type: 'buyer', active: true, limit: 100 }),
  });
  const buyers: Company[] = (buyersData as { companies?: Company[] })?.companies || [];

  const { data, isLoading, refetch } = useQuery<ShipmentsByStatusResponse>({
    queryKey: ['shipments-by-status', selectedStatuses, etaRange, shipmentRange, selectedSupplier, selectedBuyer],
    queryFn: () => {
      const params: { statuses?: string; etaFrom?: string; etaTo?: string; rangeFrom?: string; rangeTo?: string; supplierId?: string; buyerId?: string } = {};
      if (selectedStatuses.length > 0) params.statuses = selectedStatuses.join(',');
      if (etaRange?.[0]) params.etaFrom = etaRange[0].format('YYYY-MM-DD');
      if (etaRange?.[1]) params.etaTo = etaRange[1].format('YYYY-MM-DD');
      if (shipmentRange?.[0]) params.rangeFrom = shipmentRange[0].format('YYYY-MM-DD');
      if (shipmentRange?.[1]) params.rangeTo = shipmentRange[1].format('YYYY-MM-DD');
      if (selectedSupplier) params.supplierId = selectedSupplier;
      if (selectedBuyer) params.buyerId = selectedBuyer;
      return reportsApi.getShipmentsByStatus(params);
    },
  });

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('atlas_token') || localStorage.getItem('auth_token');
      const filters: Record<string, string> = {};
      if (selectedStatuses.length > 0) filters.statuses = selectedStatuses.join(',');
      if (etaRange?.[0]) filters.etaFrom = etaRange[0].format('YYYY-MM-DD');
      if (etaRange?.[1]) filters.etaTo = etaRange[1].format('YYYY-MM-DD');
      if (shipmentRange?.[0]) filters.rangeFrom = shipmentRange[0].format('YYYY-MM-DD');
      if (shipmentRange?.[1]) filters.rangeTo = shipmentRange[1].format('YYYY-MM-DD');
      if (selectedSupplier) filters.supplierId = selectedSupplier;
      if (selectedBuyer) filters.buyerId = selectedBuyer;

      const response = await fetch('/api/v1/export/excel', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reportType: 'shipments-by-status',
          filters,
        }),
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `shipments-by-status-${dayjs().format('YYYY-MM-DD')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      message.success('Report exported successfully');
    } catch (error) {
      message.error('Failed to export report');
    }
  };

  const formatNumber = (num: number) => num.toLocaleString();
  const formatDate = (date: string | null) => date ? dayjs(date).format('MMM D, YYYY') : '-';

  const columns: ColumnsType<ShipmentItem> = [
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      render: (ref, record) => (
        <a onClick={() => navigate(`/contracts/${record.contractRef}`)} style={{ cursor: 'pointer' }}>
          {ref}
        </a>
      ),
      sorter: (a, b) => a.reference.localeCompare(b.reference),
    },
    {
      title: 'Shipment',
      key: 'shipmentDate',
      render: (_, record) => formatShipmentDate(record.start, record.end),
      sorter: (a, b) => {
        const aDate = a.start || '';
        const bDate = b.start || '';
        return aDate.localeCompare(bDate);
      },
    },
    {
      title: 'Supplier',
      dataIndex: 'supplierName',
      key: 'supplierName',
      render: (name, record) => (
        <Space>
          <Text>{name}</Text>
          {record.supplierCode && <Text type="secondary">({record.supplierCode})</Text>}
        </Space>
      ),
      sorter: (a, b) => a.supplierName.localeCompare(b.supplierName),
    },
    {
      title: 'Buyer',
      dataIndex: 'buyerName',
      key: 'buyerName',
      render: (name, record) => (
        <Space>
          <Text>{name}</Text>
          {record.buyerCode && <Text type="secondary">({record.buyerCode})</Text>}
        </Space>
      ),
      sorter: (a, b) => a.buyerName.localeCompare(b.buyerName),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <StatusPill variant={getStatusVariant(status)} size="small">{status}</StatusPill>,
      sorter: (a, b) => a.status.localeCompare(b.status),
    },
    {
      title: 'Booking #',
      dataIndex: 'bookingNumber',
      key: 'bookingNumber',
      render: (val) => val || '-',
    },
    {
      title: 'ETD',
      dataIndex: 'etd',
      key: 'etd',
      render: formatDate,
      sorter: (a, b) => {
        if (!a.etd) return 1;
        if (!b.etd) return -1;
        return a.etd.localeCompare(b.etd);
      },
    },
    {
      title: 'ETA',
      dataIndex: 'eta',
      key: 'eta',
      render: formatDate,
      sorter: (a, b) => {
        if (!a.eta) return 1;
        if (!b.eta) return -1;
        return a.eta.localeCompare(b.eta);
      },
    },
    {
      title: 'Lbs',
      dataIndex: 'totalLbs',
      key: 'totalLbs',
      align: 'right',
      render: (lbs) => formatNumber(Math.round(lbs)),
      sorter: (a, b) => a.totalLbs - b.totalLbs,
    },
    {
      title: 'Containers',
      dataIndex: 'containerCount',
      key: 'containerCount',
      align: 'right',
      sorter: (a, b) => a.containerCount - b.containerCount,
    },
  ];

  return (
    <div style={{ padding: spacing['2xl'], background: colors.background.primary, minHeight: '100vh' }}>
      <div style={{ maxWidth: 1600, margin: '0 auto' }}>
        <div style={{ marginBottom: spacing.xl, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <Title>Shipments by Status</Title>
            <Text type="secondary">
              View and filter all shipments by their current status
            </Text>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              Refresh
            </Button>
            <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport}>
              Export to Excel
            </Button>
          </Space>
        </div>

        <Card style={{ marginBottom: spacing.lg }}>
          <Row gutter={[16, 12]} align="middle">
            <Col>
              <Text strong>Status:</Text>
            </Col>
            <Col flex="250px">
              <Select
                mode="multiple"
                allowClear
                placeholder="All statuses"
                value={selectedStatuses}
                onChange={setSelectedStatuses}
                style={{ width: '100%' }}
                options={STATUS_ORDER.filter(s => (data?.allStatuses || []).includes(s)).map(s => ({ label: s, value: s }))}
              />
            </Col>
            <Col>
              <Text strong>Supplier:</Text>
            </Col>
            <Col flex="200px">
              <Select
                allowClear
                showSearch
                placeholder="All suppliers"
                value={selectedSupplier}
                onChange={setSelectedSupplier}
                style={{ width: '100%' }}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={suppliers.map(s => ({ 
                  label: s.shortName || s.name, 
                  value: s.id 
                }))}
              />
            </Col>
            <Col>
              <Text strong>Buyer:</Text>
            </Col>
            <Col flex="200px">
              <Select
                allowClear
                showSearch
                placeholder="All buyers"
                value={selectedBuyer}
                onChange={setSelectedBuyer}
                style={{ width: '100%' }}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={buyers.map(b => ({ 
                  label: b.shortName || b.name, 
                  value: b.id 
                }))}
              />
            </Col>
            <Col>
              <Text strong>Shipment:</Text>
            </Col>
            <Col>
              <RangePicker
                value={shipmentRange}
                onChange={(dates) => setShipmentRange(dates as [Dayjs | null, Dayjs | null] | null)}
                allowClear
                style={{ width: 240 }}
              />
            </Col>
            <Col>
              <Text strong>ETA:</Text>
            </Col>
            <Col>
              <RangePicker
                value={etaRange}
                onChange={(dates) => setEtaRange(dates as [Dayjs | null, Dayjs | null] | null)}
                allowClear
                style={{ width: 240 }}
              />
            </Col>
          </Row>
        </Card>

        <Row gutter={16} style={{ marginBottom: spacing.lg }}>
          {data?.statusCounts && Object.entries(data.statusCounts).map(([status, count]) => (
            <Col key={status}>
              <Card size="small">
                <Statistic
                  title={<Tag color={STATUS_COLORS[status] || 'default'}>{status}</Tag>}
                  value={count}
                />
              </Card>
            </Col>
          ))}
        </Row>

        <Row gutter={16} style={{ marginBottom: spacing.lg }}>
          <Col span={8}>
            <Card>
              <Statistic
                title="Total Shipments"
                value={data?.totals?.shipmentCount || 0}
                valueStyle={{ color: colors.primary[60] }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Total Containers"
                value={data?.totals?.containerCount || 0}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Total Weight"
                value={formatNumber(Math.round(data?.totals?.totalLbs || 0))}
                suffix="lbs"
              />
            </Card>
          </Col>
        </Row>

        <Card>
          <Table
            columns={columns}
            dataSource={data?.shipments || []}
            rowKey="id"
            loading={isLoading}
            pagination={{
              pageSize: 50,
              showSizeChanger: true,
              showTotal: (total) => `${total} shipments`,
            }}
            scroll={{ x: 1200 }}
          />
        </Card>
      </div>
    </div>
  );
};

export default ShipmentsByStatus;
